import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StatePromptLogService } from '../../providers/state-prompt-log.service';

@Component({
  selector: 'app-age-verification-modal',
  templateUrl: './age-verification-modal.component.html',
  styleUrls: ['./age-verification-modal.component.css']
})
export class AgeVerificationModalComponent implements OnInit {
  ageForm: FormGroup;
  monthError = false;
  dayError = false;
  yearError = false;
  stateError = false;
  checkboxError = false;
  submitted = false;
  proUser = false;
  /**
   * Whether this presentation asks for a date of birth at all.
   *
   * Callers used to express this as `proUser` — a Pro subscriber was assumed
   * to be age-verified already, so the modal collected state only. But the
   * question the modal actually needs answered is "is a DOB missing?", which
   * is not the same question: the Lobby's 3rd-contest gate asks for state
   * alone whenever the server already holds a date of birth, whatever tier the
   * user is on.
   *
   * `collectDateOfBirth` states it directly. `proUser` still works and still
   * means what it meant, so existing call sites are unaffected.
   */
  collectDob = true;
  /**
   * SG-177 launch fix (finding 8): every failure in this modal used to be
   * signalled by red styling alone, and the two distinct checkbox failures
   * ("you ticked 18+ but your DOB says otherwise" vs "you are 18+ but did not
   * tick the box") looked identical. This carries the specific reason so the
   * user is never left staring at a red box with no explanation.
   */
  errorMessage = '';
  usStates = ["AL - Alabama", "AK - Alaska", "AZ - Arizona", "AR - Arkansas", "CA - California", "CO - Colorado", "CT - Connecticut", "DE - Delaware",
    "DC - District of Columbia", "FL - Florida", "GA - Georgia", "HI - Hawaii", "ID - Idaho", "IL - Illinois", "IN - Indiana", "IA - Iowa", "KS - Kansas", "KY - Kentucky",
    "LA - Louisiana", "ME - Maine", "MD - Maryland", "MA - Massachusetts", "MI - Michigan", "MN - Minnesota", "MS - Mississippi", "MO - Missouri", "MT - Montana",
    "NE - Nebraska", "NV - Nevada", "NH - New Hampshire", "NJ - New Jersey", "NM - New Mexico", "NY - New York", "NC - North Carolina", "ND - North Dakota",
    "OH - Ohio", "OK - Oklahoma", "OR - Oregon", "PA - Pennsylvania", "RI - Rhode Island", "SC - South Carolina", "SD - South Dakota", "TN - Tennessee",
    "TX - Texas", "UT - Utah", "VT - Vermont", "VA - Virginia", "WA - Washington", "WV - West Virginia", "WI - Wisconsin", "WY - Wyoming"
  ];

  constructor(
    public dialogRef: MatDialogRef<AgeVerificationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private statePromptLog: StatePromptLogService
  ) {
    this.proUser = data && data.proUser === true;
    this.collectDob = (data && typeof data.collectDateOfBirth === 'boolean')
      ? data.collectDateOfBirth
      : !this.proUser;

    // Pre-fill whatever is already known about this user. A state saved on
    // their profile wins over the geolocated one: the field is labelled "State
    // of Residence", so it should show the state they told us they live in,
    // not the state they happen to be standing in today.
    var initialState = this.findMatchingState(
      (data && data.existingState) ? data.existingState : (data && data.detectedState));

    // Same for the date of birth. Presenting an empty form to a user who set
    // theirs months ago reads as the app having lost it.
    var initialDob = this.splitDateOfBirth(data && data.existingDateOfBirth);

    if (this.collectDob) {
      this.ageForm = this.fb.group({
        month: [initialDob.month, [Validators.required, Validators.min(1), Validators.max(12)]],
        day: [initialDob.day, [Validators.required, Validators.min(1), Validators.max(31)]],
        year: [initialDob.year, [Validators.required, Validators.min(1900), Validators.max(new Date().getFullYear())]],
        stateOfResidence: [initialState, Validators.required],
        confirmCheckbox: [false, Validators.requiredTrue]
      });
    } else {
      // State only.
      this.ageForm = this.fb.group({
        month: [''],
        day: [''],
        year: [''],
        stateOfResidence: [initialState, Validators.required],
        confirmCheckbox: [true]
      });
    }
  }

  /**
   * Resolve a state to the exact usStates entry the <select> can show.
   *
   * Accepts either the profile's stored "XX - Name" form or a bare code/name
   * from geolocation, and matches on the code and the name SEPARATELY and
   * EXACTLY. The previous version substring-matched the whole entry, which
   * quietly mis-selected: "OR" matched "CO - Colorado" (…col-OR-ado) before it
   * reached Oregon, and "IA" matched "CA - Californ-IA". Both pre-filled a
   * state the user does not live in, on a field that gates territory
   * eligibility — so a wrong guess here is worse than no guess.
   */
  findMatchingState(value: string): string {
    if (!value) return '';
    var raw = String(value).trim();
    if (!raw) return '';

    var code = raw;
    var name = raw;
    if (raw.indexOf(' - ') > -1) {
      var parts = raw.split(' - ');
      code = parts[0].trim();
      name = parts.length > 1 ? parts[1].trim() : '';
    }

    for (var i = 0; i < this.usStates.length; i++) {
      var entry = this.usStates[i];
      var entryParts = entry.split(' - ');
      var entryCode = entryParts[0].trim();
      var entryName = entryParts.length > 1 ? entryParts[1].trim() : '';

      if (entryCode.toLowerCase() === code.toLowerCase()) return entry;
      if (name && entryName.toLowerCase() === name.toLowerCase()) return entry;
    }
    return '';
  }

  /**
   * Split a stored date of birth into the modal's three fields.
   *
   * The "YYYY-MM-DD" prefix is read textually rather than through Date: the
   * profile stores a calendar date, and `new Date('1990-05-12')` parses it as
   * UTC midnight, which in any negative-offset timezone renders as 11 May —
   * pre-filling the user's birthday one day early.
   */
  private splitDateOfBirth(dob: any): { month: string, day: string, year: string } {
    var empty = { month: '', day: '', year: '' };
    if (!dob) return empty;

    var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob));
    if (match) {
      return {
        year: match[1],
        month: String(Number(match[2])),
        day: String(Number(match[3]))
      };
    }

    var parsed = new Date(dob);
    if (!isNaN(parsed.getTime())) {
      return {
        year: String(parsed.getFullYear()),
        month: String(parsed.getMonth() + 1),
        day: String(parsed.getDate())
      };
    }
    return empty;
  }

  ngOnInit(): void {
    // Log every PRESENTATION of the State of Residence prompt.
    //
    // Done here, in the modal itself, rather than at the call sites: there
    // are eight of them across the Lobby and Edit Profile, and any missed one
    // would leave a silent gap. A gap is worse than no logging at all —
    // it reads as "we never prompted this user", which is precisely the
    // claim under investigation.
    //
    // The state field is unconditional in this component (both the proUser
    // and the DOB variants render it), so every presentation genuinely is a
    // State of Residence prompt.
    //
    // Fire-and-forget by design: userId, timestamp and the profile values are
    // resolved server-side, and a logging failure must not disturb the prompt
    // the user is looking at.
    this.statePromptLog.logPromptShown(this.data && this.data.promptSource);
  }

  /**
   * Calculate if user is 18+ years old
   */
  isUserOver18(): boolean {
    const monthControl = this.ageForm.get('month');
    const dayControl = this.ageForm.get('day');
    const yearControl = this.ageForm.get('year');

    if (!monthControl || !dayControl || !yearControl) {
      return false;
    }

    const month = parseInt(monthControl.value);
    const day = parseInt(dayControl.value);
    const year = parseInt(yearControl.value);

    if (!month || !day || !year) {
      return false;
    }

    const today = new Date();
    const birthDate = new Date(year, month - 1, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 18;
  }

  /**
   * Validate all fields and checkbox match
   */
  validateFields(): boolean {
    this.submitted = true;
    this.errorMessage = '';

    const stateControl = this.ageForm.get('stateOfResidence');
    this.stateError = stateControl ? !stateControl.valid : true;

    if (!this.collectDob) {
      // State-only presentation: state is the only required field.
      if (this.stateError) {
        this.errorMessage = 'Please select your State of residence.';
      }
      return !this.stateError;
    }

    const monthControl = this.ageForm.get('month');
    const dayControl = this.ageForm.get('day');
    const yearControl = this.ageForm.get('year');
    const checkboxControl = this.ageForm.get('confirmCheckbox');

    this.monthError = monthControl ? !monthControl.valid : true;
    this.dayError = dayControl ? !dayControl.valid : true;
    this.yearError = yearControl ? !yearControl.valid : true;

    // A date that passes the per-field ranges can still not exist (31 February,
    // 30 February in a leap year, ...). JS Date silently rolls those over to the
    // following month, which would compute an age for a birthday the user never
    // had -- reject them explicitly and flag the offending fields.
    if (!this.monthError && !this.dayError && !this.yearError && !this.isRealCalendarDate()) {
      this.dayError = true;
      this.monthError = true;
    }

    // Check if all date and state fields are filled
    const allFieldsFilled = !this.monthError && !this.dayError && !this.yearError && !this.stateError;

    // Check if checkbox matches the calculated age
    const isOver18 = this.isUserOver18();
    const checkboxChecked = checkboxControl ? checkboxControl.value : false;

    // Mismatch: checkbox checked but DOB shows under 18
    this.checkboxError = checkboxChecked && !isOver18;

    if (!allFieldsFilled) {
      const dateBad = this.monthError || this.dayError || this.yearError;
      if (dateBad && this.stateError) {
        this.errorMessage = 'Please enter a valid date of birth and select your State of residence.';
      } else if (dateBad) {
        this.errorMessage = 'Please enter a valid date of birth using MM / DD / YYYY.';
      } else {
        this.errorMessage = 'Please select your State of residence.';
      }
    } else if (this.checkboxError) {
      // Set here too so the message is present even on the validateFields()
      // path -- onContinue() refines it for the two distinct checkbox cases.
      this.errorMessage = 'The date of birth you entered says you are under 18, '
        + 'but you ticked the 18-or-older box. Please correct whichever is wrong.';
    }

    return allFieldsFilled;
  }

  /**
   * True when month/day/year describe a date that actually exists. Guards the
   * JS Date rollover (e.g. new Date(2000, 1, 31) becomes 2 March 2000).
   */
  private isRealCalendarDate(): boolean {
    const monthControl = this.ageForm.get('month');
    const dayControl = this.ageForm.get('day');
    const yearControl = this.ageForm.get('year');

    if (!monthControl || !dayControl || !yearControl) {
      return false;
    }

    const month = parseInt(monthControl.value, 10);
    const day = parseInt(dayControl.value, 10);
    const year = parseInt(yearControl.value, 10);

    if (!month || !day || !year) {
      return false;
    }

    const probe = new Date(year, month - 1, day);
    return probe.getFullYear() === year
      && probe.getMonth() === month - 1
      && probe.getDate() === day;
  }

  /**
   * Handle continue button click
   */
  onContinue(): void {
    if (this.validateFields()) {
      const stateControl = this.ageForm.get('stateOfResidence');

      if (!this.collectDob) {
        // State-only presentation: no date of birth was asked for, so report
        // the state and nothing else. isOver18 stays true because this form is
        // only shown to users whose age is already established elsewhere.
        this.dialogRef.close({
          action: 'verified',
          isOver18: true,
          stateOfResidence: stateControl ? stateControl.value : ''
        });
        return;
      }

      const isOver18 = this.isUserOver18();
      const monthControl = this.ageForm.get('month');
      const dayControl = this.ageForm.get('day');
      const yearControl = this.ageForm.get('year');

      const dateOfBirth = {
        month: monthControl ? monthControl.value : '',
        day: dayControl ? dayControl.value : '',
        year: yearControl ? yearControl.value : ''
      };

      const checkboxControl = this.ageForm.get('confirmCheckbox');
      const checkboxChecked = checkboxControl ? checkboxControl.value : false;

      // Contradictory values: under-18 DOB but checkbox says 18+ — show red mismatch, keep modal open
      if (!isOver18 && checkboxChecked) {
        this.checkboxError = true;
        this.errorMessage = 'The date of birth you entered says you are under 18, '
          + 'but you ticked the 18-or-older box. Please correct whichever is wrong.';
        return;
      }

      // Under 18 and checkbox not checked — close with under_18 action
      if (!isOver18) {
        this.dialogRef.close({
          action: 'under_18',
          isOver18: false,
          dateOfBirth: dateOfBirth,
          stateOfResidence: stateControl ? stateControl.value : ''
        });
        return;
      }

      // 18+ but checkbox not checked — show checkbox error, keep modal open
      if (!checkboxChecked) {
        this.checkboxError = true;
        this.errorMessage = 'Please tick the box to confirm you are 18 years of age or older.';
        return;
      }

      // 18+ and checkbox checked — verified
      this.dialogRef.close({
        action: 'verified',
        isOver18: true,
        dateOfBirth: dateOfBirth,
        stateOfResidence: stateControl ? stateControl.value : ''
      });
    }
  }

  /**
   * Handle maybe later click
   */
  onMaybeLater(): void {
    this.dialogRef.close({ action: 'dismissed' });
  }

  /**
   * Close modal
   */
  close(): void {
    this.dialogRef.close({ action: 'closed' });
  }
}
